import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Pencil, Trash2, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "", capacity: "", price: "", copy: "", image: "", description: "", features_text: "", published: true,
};

const VehicleForm = ({ vehicle, onClose, onSaved }) => {
  const [form, setForm] = useState(
    vehicle
      ? { ...EMPTY, ...vehicle, features_text: (vehicle.features || []).join("\n") }
      : EMPTY
  );
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls =
    "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-[var(--brand)]/30 focus:border-[var(--brand)] transition-all";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.capacity.trim() || !form.price || !form.image.trim()) {
      toast.error("Name, capacity, price and image URL are required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        capacity: form.capacity,
        price: Number(form.price),
        copy: form.copy,
        image: form.image,
        description: form.description,
        features: form.features_text.split("\n").map((s) => s.trim()).filter(Boolean),
        published: !!form.published,
      };
      if (vehicle) await axios.put(`${API}/fleet/${vehicle.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/fleet`, payload, { withCredentials: true });
      toast.success(vehicle ? "Vehicle updated." : "Vehicle added to the fleet.");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the vehicle.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="fleet-form-overlay">
      <div className="absolute inset-0 bg-[#0b132b]/50 backdrop-blur-sm" onClick={onClose} />
      <motion.form
        onSubmit={submit}
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 24, opacity: 0 }}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-6 sm:p-8 space-y-4 shadow-2xl"
        data-testid="fleet-form"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl text-[#0b132b]">{vehicle ? "Edit Vehicle" : "Add Vehicle"}</h3>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors" data-testid="fleet-form-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="fleet-form-name" value={form.name} onChange={set("name")} placeholder="Vehicle name * (e.g. Mercedes V-Class)" className={inputCls} />
          <input data-testid="fleet-form-capacity" value={form.capacity} onChange={set("capacity")} placeholder="Capacity * (e.g. Up to 7 guests)" className={inputCls} />
          <input data-testid="fleet-form-price" type="number" min="0" value={form.price} onChange={set("price")} placeholder="From price per day *" className={inputCls} />
        </div>
        <input data-testid="fleet-form-copy" value={form.copy} onChange={set("copy")} placeholder="Short tagline (shown on the fleet card)" className={inputCls} />
        <input data-testid="fleet-form-image" value={form.image} onChange={set("image")} placeholder="Photo URL * (upload in Media, paste here)" className={inputCls} />
        <textarea data-testid="fleet-form-description" rows={3} value={form.description} onChange={set("description")} placeholder="Longer description for the details page" className={`${inputCls} resize-none`} />
        <textarea
          data-testid="fleet-form-features"
          rows={5}
          value={form.features_text}
          onChange={(e) => setForm({ ...form, features_text: e.target.value })}
          placeholder={"Features — one per line\nLeather captain's chairs\nWi-Fi on board"}
          className={`${inputCls} resize-y`}
        />
        <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-600 cursor-pointer">
          <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-4 h-4 accent-[var(--brand)]" data-testid="fleet-form-published" />
          Published (visible on the website)
        </label>
        <button data-testid="fleet-form-submit" disabled={busy} className="w-full rounded-full bg-[var(--brand)] text-white text-sm font-bold py-3.5 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-60">
          {busy ? "Saving…" : vehicle ? "Save Changes" : "Add Vehicle"}
        </button>
      </motion.form>
    </motion.div>
  );
};

export const FleetTab = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(undefined);
  const { refreshFleet } = useSiteData();

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/fleet/all`, { withCredentials: true });
      setItems(data);
    } catch {
      setItems([]);
    }
    refreshFleet();
  };
  useEffect(() => {
    load();
  }, []);

  const togglePublish = async (v) => {
    try {
      await axios.put(
        `${API}/fleet/${v.id}`,
        { name: v.name, capacity: v.capacity, price: v.price, copy: v.copy || "", image: v.image, description: v.description || "", features: v.features || [], published: !v.published },
        { withCredentials: true }
      );
      toast.success(v.published ? "Vehicle hidden." : "Vehicle published.");
      load();
    } catch {
      toast.error("Could not update the vehicle.");
    }
  };

  const del = async (v) => {
    if (!window.confirm(`Remove "${v.name}" from the fleet? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/fleet/${v.id}`, { withCredentials: true });
      toast.success("Vehicle removed.");
      load();
    } catch {
      toast.error("Could not remove the vehicle.");
    }
  };

  return (
    <div data-testid="admin-fleet-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm font-semibold text-slate-500">{items ? items.length : 0} vehicles — each gets its own details page at /fleet/name</p>
        <button
          data-testid="fleet-add-button"
          onClick={() => setEditing(null)}
          className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)]"
        >
          <Plus className="w-4 h-4" /> Add Vehicle
        </button>
      </div>

      {!items ? (
        <div className="py-16 text-center" data-testid="fleet-loading">
          <div className="w-10 h-10 mx-auto rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border border-slate-100 p-14 text-center" data-testid="fleet-empty-admin">
          <p className="font-display font-bold text-[#0b132b]">No vehicles yet</p>
          <p className="mt-1 text-sm text-slate-400">Add your first vehicle — it appears on the homepage fleet strip immediately.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="fleet-list">
          {items.map((v) => (
            <div key={v.id} className="rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]" data-testid={`fleet-row-${v.slug || v.id}`}>
              <img src={v.image} alt={v.name} className="w-full h-32 object-cover" />
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-bold text-base text-[#0b132b] leading-tight">{v.name}</h3>
                  <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${v.published ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                    {v.published ? "Live" : "Hidden"}
                  </span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{v.capacity} · from CHF {v.price}/day</p>
                <div className="mt-4 flex items-center gap-2">
                  <button data-testid={`fleet-edit-${v.slug || v.id}`} onClick={() => setEditing(v)} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-xs font-bold text-slate-600 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button data-testid={`fleet-toggle-${v.slug || v.id}`} onClick={() => togglePublish(v)} className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-500 hover:border-[var(--brand)] hover:text-[var(--brand)] transition-colors" aria-label={v.published ? "Hide" : "Publish"}>
                    {v.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button data-testid={`fleet-delete-${v.slug || v.id}`} onClick={() => del(v)} className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-colors" aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing !== undefined && (
          <VehicleForm
            vehicle={editing}
            onClose={() => setEditing(undefined)}
            onSaved={() => {
              setEditing(undefined);
              load();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
